import type {
  LocationQueryRaw,
  NavigationGuardWithThis,
  RouteLocationNormalized,
  Router,
} from 'vue-router'
import { i18n } from '~/locales'
import { WORKSPACE_HOME, WORKSPACE_PROFILE, safeNext } from '~/modules/auth/constants'
import type { Workspace } from '~/modules/auth/types'
import type { FlagKey } from '~/modules/rollout/constants'
import { resolveLegacyRedirect } from '~/modules/rollout/legacy-redirects'
import { useAuthStore } from '~/stores/auth'
import { useUiStore } from '~/stores/ui'

/**
 * Route guards, in order: legacy bridge → bootstrap → auth → workspace → capability → flag →
 * password setup.
 *
 * > **Guards are UX, not security.** Every check here exists again on the backend, and the app
 * > must behave correctly when one is bypassed: an API 403 renders the forbidden state, never a
 * > blank screen. Hiding a button is not a control.
 *
 * They are registered as a single `beforeEach` rather than five, because vue-router runs
 * separate guards in registration order but each has to re-derive the store and the route
 * meta — and, more importantly, because the *order* is the specification and one function makes
 * it readable.
 */
export function registerGuards(router: Router) {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    // 0. Legacy bridge (frontend phase-14 §2.2). A bookmark or an emailed link pointing at a
    //    legacy portal path lands on the V2 equivalent instead of the not-found page. Runs
    //    before everything else, including the bootstrap, because the destination may be
    //    public and there is no sense loading a session to redirect a marketplace link.
    //    A legacy path with no V2 equivalent is deliberately NOT redirected — it falls through
    //    to the not-found page, and `UNMAPPED` records why.
    const bridged = resolveLegacyRedirect(to.path)
    if (bridged && bridged !== to.fullPath) {
      return { path: bridged.split('?')[0], query: queryOf(bridged, to.query), replace: true }
    }

    // 1. Bootstrap. Once per session, before any guarded navigation, so no guard decides on a
    //    session it has not loaded — which is what causes a flash of the wrong dashboard.
    if (!auth.bootstrapped) {
      await auth.bootstrap()
    }

    // 2. Authentication.
    if (needsAuth(to) && !auth.isAuthenticated) {
      return { path: '/auth/login', query: { next: to.fullPath } }
    }

    // An authenticated user has no business on the login or code pages.
    if (auth.isAuthenticated && isSignInPage(to)) {
      return homeFor(auth)
    }

    if (!auth.isAuthenticated) return true

    // 3. Workspace. `/app` itself is the redirector to the default workspace.
    if (to.path === '/app' || to.path === '/app/') {
      return homeFor(auth)
    }
    // …and `/app/settings/password` (with the legacy links mapped to it) is the redirector to
    // the active workspace's own profile, on its Security tab. It used to be a standalone page
    // outside every workspace's settings; the form now lives in one place per workspace.
    if (to.name === 'settings-password') {
      return profileFor(auth, 'security')
    }

    const required = to.meta.workspace as Workspace | undefined
    if (required && !auth.hasWorkspace(required)) {
      // Say what happened. Silently landing somewhere else reads as a broken link.
      useUiStore().notify(
        'info',
        i18n.global.t('workspaces.denied', {
          workspace: i18n.global.t(`workspaces.${required}.title`),
        }),
      )
      return { ...homeFor(auth), query: { denied: required } }
    }

    // 4. Capability.
    const capability = to.meta.capability as string | undefined
    if (capability && !auth.can(capability)) {
      return { path: '/403' }
    }

    // 5. Feature flag (frontend phase-14 §2.1). A flagged-off feature is **absent**, not
    //    disabled: the route does not resolve, and the menu entry that would have led here is
    //    filtered out by the same flag. The server refuses it too — this is UX.
    const flag = to.meta.flag as FlagKey | undefined
    if (flag && !auth.isFlagEnabled(flag)) {
      return { name: 'not-found', params: { pathMatch: to.path.slice(1).split('/') } }
    }

    // 6. A confirmed WhatsApp number or email (2026-10-27, scope-freeze.md D25). A landlord's
    //    dashboard waits for one: the server answers `auth_contact_unconfirmed` until then, and
    //    this sends them to the step that fixes it instead of to a page of errors.
    if (auth.contacts?.must_confirm && isDashboardRoute(to) && to.name !== 'auth-confirm') {
      return { path: '/auth/confirm', query: { next: to.fullPath } }
    }

    // 7. Password setup. Offered once per session — a user who skipped is not nagged on every
    //    navigation, and is prompted again next sign-in because the flag is session-scoped.
    if (
      auth.requiresPasswordSetup &&
      !auth.passwordSetupSkipped &&
      isDashboardRoute(to) &&
      to.name !== 'auth-password-set'
    ) {
      return { path: '/auth/password/set', query: { next: to.fullPath } }
    }

    return true
  })
}

/**
 * The query a redirect destination carries, merged with whatever the incoming URL had.
 *
 * Some destinations in the map are "the same screen with a tab or filter preselected"
 * (`/app/landlord/analytics?report=revenue`). The incoming query wins on a collision: the user's
 * own link is more specific than the map's default.
 */
function queryOf(
  destination: string,
  incoming: RouteLocationNormalized['query'],
): LocationQueryRaw {
  const [, search] = destination.split('?')
  if (!search) return incoming
  const merged: LocationQueryRaw = {}
  for (const [key, value] of new URLSearchParams(search)) merged[key] = value
  return { ...merged, ...incoming }
}

/** A route as the guards read it — a navigation target, or anything `router.resolve` returns. */
type Destination = Pick<RouteLocationNormalized, 'path' | 'meta'> & {
  name?: RouteLocationNormalized['name'] | null
}

/**
 * Would the guards turn a navigation to `to` straight around? Only the two redirects that
 * depend on nothing but the route and whether you are signed in: a signed-in page while signed
 * out, and the sign-in pages while signed in. The auth screens' "Back" asks this so it never
 * returns to a page that sends you back where you started.
 */
export function isTurnedAway(to: Destination, isAuthenticated: boolean): boolean {
  return isAuthenticated ? isSignInPage(to) : needsAuth(to)
}

function needsAuth(to: Destination): boolean {
  if (to.meta.public) return false
  return Boolean(to.meta.requiresAuth) || to.path.startsWith('/app')
}

function isSignInPage(to: Destination): boolean {
  return to.name === 'auth-login' || to.name === 'auth-otp'
}

function isDashboardRoute(to: RouteLocationNormalized): boolean {
  return to.path.startsWith('/app')
}

/**
 * Where an authenticated user belongs.
 *
 * From the **session**, never from a client-side assumption about what a role implies. An
 * account with no workspace gets the explainer, not a redirect loop.
 */
function homeFor(auth: ReturnType<typeof useAuthStore>) {
  const workspace = auth.activeWorkspace ?? auth.defaultWorkspace
  if (!workspace) return { path: '/auth/no-workspace' }
  return { path: safeNext(WORKSPACE_HOME[workspace], '/auth/no-workspace') }
}

/** The active workspace's profile page, optionally on one of its tabs. */
function profileFor(auth: ReturnType<typeof useAuthStore>, tab?: string) {
  const workspace = auth.activeWorkspace ?? auth.defaultWorkspace
  const path = workspace ? WORKSPACE_PROFILE[workspace] : undefined
  if (!path) return { path: '/auth/no-workspace' }
  return { path, query: tab ? { tab } : {}, replace: true }
}

export type { NavigationGuardWithThis }
